import { canReceiveSms } from 'utils/smsPhone';

// Pure logic copied from gastronaut-api NoShowProtection/domain (templates.ts,
// fees.ts) so previews and caps match the server. The server stays binding.
import {
  AgreedTerms,
  Claim,
  ClaimStatus,
  FeeTier,
  Liability,
  LiabilityKind,
  NoShowTemplate,
  ProtectionStatus,
} from 'types/noShowProtection';

type Language = 'de' | 'en';

const COPY = {
  de: {
    intro:
      'Für diese Reservierung hinterlegen Sie ein Zahlungsmittel. Das Restaurant kann Gebühren einfordern bei',
    fullCancellation: 'Stornierung',
    guestReduction: 'Verringerung der Gästezahl',
    noShow: 'Nichterscheinen (auch einzelner Gäste)',
    and: 'und',
    tier: (window: string, fee: string) =>
      `Änderung weniger als ${window} vor Reservierungsbeginn: ${fee}`,
    noShowTier: (fee: string) => `Nichterscheinen: ${fee}`,
    free: (window: string) =>
      `Bis ${window} vor Reservierungsbeginn ist eine Änderung kostenlos.`,
    perPerson: 'pro Person',
    perGroup: 'pro Reservierung (Obergrenze für die gesamte Reservierung)',
    days: (n: number) => `${n} Tage`,
    hours: (n: number) => (n === 1 ? '1 Stunde' : `${n} Stunden`),
    money: (cents: number) =>
      `${(cents / 100)
        .toFixed(2)
        .replace('.', ',')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`,
    outro: 'Mit Ihrer Zustimmung ermächtigen Sie das Restaurant, die vereinbarten Gebühren später über das hinterlegte Zahlungsmittel abzubuchen. Eine Abbuchung erfolgt nur manuell durch das Restaurant.',
  },
  en: {
    intro:
      'You leave a payment method for this reservation. The restaurant may charge a fee for',
    fullCancellation: 'cancellation',
    guestReduction: 'reducing the number of guests',
    noShow: 'not showing up (also individual guests)',
    and: 'and',
    tier: (window: string, fee: string) =>
      `Changes less than ${window} before the reservation: ${fee}`,
    noShowTier: (fee: string) => `No-show: ${fee}`,
    free: (window: string) =>
      `Changes up to ${window} before the reservation are free of charge.`,
    perPerson: 'per person',
    perGroup: 'per reservation (cap for the whole reservation)',
    days: (n: number) => `${n} days`,
    hours: (n: number) => (n === 1 ? '1 hour' : `${n} hours`),
    money: (cents: number) => `${(cents / 100).toFixed(2)} €`,
    outro: 'By agreeing, you authorise the restaurant to charge the agreed fees to your stored payment method later. Any charge is made manually by the restaurant.',
  },
};

function formatWindow(hours: number, copy: typeof COPY['de']) {
  return hours > 48 && hours % 24 === 0
    ? copy.days(hours / 24)
    : copy.hours(hours);
}

function joinList(items: string[], and: string) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

export function buildGuestText(
  template: NoShowTemplate,
  language: string
): string {
  const lang: Language = language === 'de' ? 'de' : 'en';
  const copy = COPY[lang];
  const unit = template.unit === 'perPerson' ? copy.perPerson : copy.perGroup;
  const fee = (tier: FeeTier) => `${copy.money(tier.amountCents)} ${unit}`;

  const events = (['fullCancellation', 'guestReduction', 'noShow'] as const)
    .filter((key) => template.triggers[key])
    .map((key) => copy[key]);

  const lines = [`${copy.intro} ${joinList(events, copy.and)}:`];
  template.tiers.forEach((tier) =>
    lines.push(`– ${copy.tier(formatWindow(tier.hoursBefore, copy), fee(tier))}`)
  );
  const lastTier = template.tiers[template.tiers.length - 1];
  if (template.triggers.noShow && lastTier)
    lines.push(`– ${copy.noShowTier(fee(lastTier))}`);
  if (template.tiers[0])
    lines.push(copy.free(formatWindow(template.tiers[0].hoursBefore, copy)));
  lines.push(copy.outro);

  const extra = template.extraText?.[lang] ?? template.extraText?.de;
  if (extra?.trim()) lines.push(extra.trim());

  return lines.join('\n');
}

const HOUR = 60 * 60 * 1000;

function feePerUnitAt(tiers: FeeTier[], startTs: number, atTs: number) {
  const hoursBeforeStart = (startTs - atTs) / HOUR;
  const applicable = tiers.filter((tier) => hoursBeforeStart < tier.hoursBefore);
  if (!applicable.length) return 0;
  return applicable[applicable.length - 1].amountCents;
}

/** Guests that can still be missing: on the reservation now, not yet claimed as missing. Mirrors the API check. */
export function guestsThatCanBeMissing(currentGuests: number, liabilities: Liability[]) {
  const alreadyMissing = liabilities.filter((l) => l.kind === 'noShow').reduce((sum, l) => sum + l.persons, 0);
  return Math.max(0, currentGuests - alreadyMissing);
}

export function personsStillCovered(
  terms: AgreedTerms,
  liabilities: Liability[]
) {
  const used = liabilities.reduce((sum, l) => sum + l.persons, 0);
  return Math.max(0, terms.guests - used);
}

const countsTowardCap = (claim: Claim) => claim.status !== 'cancelled';

export type ClaimCap = {
  /** What the agreement allows for this occasion in total. */
  agreedCents: number;
  /** Already claimed against the same cap (excluding cancelled claims). */
  claimedCents: number;
  /** What may still be claimed now. */
  remainingCents: number;
  persons: number;
};

/**
 * Same rules as the API's recordLiability + claimableCents: an occasion that
 * already has a liability keeps its cap, a new one is priced at the time it
 * happened; a group fee is one shared cap for the whole reservation.
 */
export function claimCap(input: {
  terms: AgreedTerms;
  liabilities: Liability[];
  claims: Claim[];
  kind: LiabilityKind;
  observationId?: string;
  liabilityId?: string;
  occurredAt: number;
  persons?: number;
}): ClaimCap | null {
  const { terms, liabilities, claims, kind } = input;
  if (!terms.triggers[kind]) return null;

  const existingId = input.liabilityId ?? (input.observationId ? `obs:${input.observationId}` : undefined);
  const existing = existingId ? liabilities.find((l) => l.id === existingId) : undefined;

  let liability = existing;
  if (!liability) {
    const available = personsStillCovered(terms, liabilities);
    const persons =
      kind === 'fullCancellation' ? available : input.persons ?? 0;
    if (!Number.isInteger(persons) || persons < 1 || persons > available) {
      return null;
    }
    const perUnit = feePerUnitAt(terms.tiers, terms.startTs, input.occurredAt);
    liability = {
      id: 'draft',
      kind,
      persons,
      occurredAt: input.occurredAt,
      capCents: terms.unit === 'perPerson' ? perUnit * persons : perUnit,
      recordedBy: '',
    };
  }

  const allLiabilities = existing ? liabilities : [...liabilities, liability];
  const activeClaims = claims.filter(countsTowardCap);

  if (terms.unit === 'perGroup') {
    const sharedCap = Math.max(0, ...allLiabilities.map((l) => l.capCents));
    const claimedCents = activeClaims.reduce((s, c) => s + c.amountCents, 0);
    return {
      agreedCents: sharedCap,
      claimedCents,
      remainingCents: Math.max(
        0,
        Math.min(liability.capCents, sharedCap - claimedCents)
      ),
      persons: liability.persons,
    };
  }

  const claimedCents = activeClaims
    .filter((c) => c.liabilityId === liability!.id)
    .reduce((s, c) => s + c.amountCents, 0);
  return {
    agreedCents: liability.capCents,
    claimedCents,
    remainingCents: Math.max(0, liability.capCents - claimedCents),
    persons: liability.persons,
  };
}

export const claimRequestedCents = (claims: Claim[]) =>
  claims
    .filter((c) => c.status !== 'cancelled')
    .reduce((sum, c) => sum + c.amountCents, 0);

export const claimCollectedCents = (claims: Claim[]) =>
  claims.reduce(
    (sum, c) => sum + c.paidCents - c.refundedCents - c.chargedBackCents,
    0
  );

/** English source strings; translated in the noShowProtection namespace. */
export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  open: 'Open',
  processing: 'Processing',
  paid: 'Paid',
  failed: 'Failed',
  cancelled: 'Cancelled',
  partially_refunded: 'Partially refunded',
  refunded: 'Refunded',
  charged_back: 'Charged back',
};

export const PROTECTION_STATUS_LABEL: Record<ProtectionStatus, string> = {
  pending: 'Deposit pending',
  secured: 'Secured',
  expired: 'Expired',
  waived: 'Released without protection',
  failed: 'Failed',
};

export const LIABILITY_KIND_LABEL: Record<LiabilityKind, string> = {
  fullCancellation: 'Cancellation',
  guestReduction: 'Reduced number of guests',
  noShow: 'No-show',
};

export const formatCents = (cents: number) =>
  `${(cents / 100)
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;

/** Parses "12,50" / "12.50" / "1.520,00" into cents; NaN when invalid. */
export const parseEuroToCents = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return NaN;
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : trimmed;
  const euros = Number(normalized);
  return Number.isFinite(euros) ? Math.round(euros * 100) : NaN;
};

export const centsToEuroInput = (cents: number) =>
  (cents / 100).toFixed(2).replace('.', ',');

export const newRequestId = (): string => {
  const cryptoApi = window.crypto as Crypto & { randomUUID?: () => string };
  return typeof cryptoApi?.randomUUID === 'function'
    ? cryptoApi.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const formatDateTime = (ts: number) =>
  new Date(ts).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const apiErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

/** Same rule as the Stripe no-show SMS: only German and Austrian numbers. */
export const canReceiveProtectionSms = (phone?: string | null) => {
  const digits = (phone ?? '')
    .replace(/\D/g, '')
    .replace(/^00/, '')
    .replace(/^0/, '49');
  return (
    canReceiveSms(phone ?? '') && digits.length >= 11 && digits.length <= 15
  );
};

export type StaffProtectionChoice = 'sms' | 'waive' | 'pendingWithoutSms';

/** Skipping the protection is always an explicit choice of a user with the charge right. */
export const staffProtectionChoice = (input: {
  waive: boolean;
  canCharge: boolean;
  phone?: string | null;
  sendSms?: boolean;
}): StaffProtectionChoice => {
  if (input.waive && input.canCharge) return 'waive';
  if (input.sendSms === true && canReceiveProtectionSms(input.phone))
    return 'sms';
  return 'pendingWithoutSms';
};

export const WAIVED_AT_CREATION_NOTE = 'Beim Anlegen ohne Schutz freigegeben';

export const staffProtectionError = (
  input: Parameters<typeof staffProtectionChoice>[0]
) => {
  if (staffProtectionChoice(input) === 'waive') return null;
  if (!canReceiveProtectionSms(input.phone))
    return 'Enter a valid German or Austrian mobile number to send the protection link.';
  if (input.sendSms !== true)
    return 'The guest must agree to receiving the protection link by SMS.';
  return null;
};
